import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReservationRow, WriteReceipt } from '@smile/shared';
import { ApiError, api } from '../api/client';
import { splitDt } from '../lib/filters';
import { CZK } from '../lib/format';

const kc = (n: number) => `${CZK.format(n)} Kč`;

/**
 * "Přijel" — the first write in the product.
 *
 * Shown only on a reservation that can arrive: active and not yet stamped
 * in. One press opens two fields, the spot and the KeyCafe box, both
 * optional, both with the legacy vocabulary offered as suggestions; a
 * second press sends it. On success the button becomes the fact — "Přijel
 * v 10:04" — and every query on the screen is refetched, so the board
 * moves the car from Příjezdy to Zaparkovalo by itself. A refusal is shown
 * in the server's own words, because a 409 that says "Vůz už je označen
 * jako přijatý" is the whole explanation and a code is not.
 *
 * Nothing optimistic. The row on screen changes when the database says it
 * changed, and not before; the desk has been burned by screens that said
 * "done" a second before "error".
 */
export function ArriveButton({ r, compact, defaultOpen }: { r: ReservationRow; compact?: boolean; defaultOpen?: boolean }) {
  const qc = useQueryClient();
  /* `defaultOpen`: the strip is shown at once, as the old board showed it
     the moment a row was opened — the dispatcher, 20 October 2026: "so
     where is the payment possibility?" (it was behind a button). */
  const [open, setOpen] = useState(defaultOpen ?? false);
  const [spot, setSpot] = useState(r.parkingTarget ?? '');
  const [keycafe, setKeycafe] = useState(r.keycafeArrival ?? '');
  // "Doporučené místo" (the old form's parkingtarget), beside Místo (= the old "Zaparkováno").
  const [target, setTarget] = useState(r.parkingTarget ?? '');
  const [carkey, setCarkey] = useState(false);
  const [receipt, setReceipt] = useState<WriteReceipt | null>(null);
  // The legacy arrival form took money too — most customers pay at check-in.
  const owed = Math.max(0, Math.round(r.outstanding));
  const [amount, setAmount] = useState(String(owed));
  const [method, setMethod] = useState<'cash' | 'card'>('cash');
  /* The Platba tick: off until the desk ticks it, as the old strip's box. */
  const [pay, setPay] = useState(false);

  const eligible = ['new', 'pending', 'paid', 'verified'].includes(r.status) && !r.arrivalReal;

  const spots = useQuery({
    queryKey: ['catalog', 'spots'],
    queryFn: () => api.spots(),
    enabled: open,
    staleTime: 10 * 60_000,
  });

  const m = useMutation({
    mutationFn: (withPayment: boolean) => {
      const n = Number(amount);
      return api.arrive(r.id, {
        ...(spot.trim() ? { spot: spot.trim() } : {}),
        ...(keycafe.trim() ? { keycafeArrival: keycafe.trim() } : {}),
        ...(target.trim() && target.trim() !== (r.parkingTarget ?? '') ? { parkingTarget: target.trim() } : {}),
        ...(carkey ? { carkey: true } : {}),
        ...(withPayment && n > 0 ? { payment: { amount: Math.round(n), method } } : {}),
      });
    },
    onSuccess: async (rc) => {
      setReceipt(rc);
      setOpen(false);
      // The board, the list, the detail: all of them show this car, and all
      // of them are now wrong until they refetch. Invalidate everything —
      // a write is rare and a stale screen after one is the worst outcome.
      await qc.invalidateQueries();
    },
  });

  /* Doporučené místo on its own, before the car comes (the manager, 30 October 2026 call: the
     dispatchers used to set it on the board together with the KeyCafe code, "one to the 7th floor,
     one outside"; KeyCafe codes are automatic now, the place is not). An edit, not an arrival —
     the booking stays in Příjezdy. */
  const tm = useMutation({
    mutationFn: () => api.edit(r.id, { parkingTarget: target.trim() }),
    onSuccess: async () => {
      await qc.invalidateQueries();
    },
  });
  const targetChanged = target.trim() !== (r.parkingTarget ?? '');

  if (receipt) {
    return (
      <span className="arrived-mark" title={`Označeno jako přijaté ${receipt.at}`}>
        ✔ Přijel v {splitDt(receipt.at).t}
      </span>
    );
  }
  if (!eligible) return null;

  const err = m.error;
  const message =
    err instanceof ApiError
      ? err.serverMessage ??
        (err.code === 'writes_disabled'
          ? 'Zápisy jsou na tomto serveru vypnuté.'
          : err.code === 'forbidden'
            ? 'Na tuhle akci nemáte oprávnění.'
            : err.message)
      : err instanceof Error
        ? err.message
        : null;

  if (!open) {
    return (
      <button
        type="button"
        className={compact ? 'btn sm primary' : 'btn primary'}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        Příjezd
      </button>
    );
  }

  // Europort floors come as {value,label}; the row stores the value ('7',
  // 'M'), so that is what the suggestion list offers.
  const options = spots.data
    ? /* Europort first (the owner, 31 Oct 2026): a car arriving is parked at Europort; the yards follow. */
      [...spots.data.europort.map((e) => ({ value: e.value, label: e.label })), ...[...spots.data.tuchomerice, ...spots.data.used].map((v) => ({ value: v, label: v }))]
    : [];

  return (
    <form
      className="arrive-form"
      onClick={(e) => e.stopPropagation()}
      onSubmit={(e) => {
        e.preventDefault();
        if (!m.isPending && spot.trim()) m.mutate(pay);
      }}
    >
      <label>
        Stání
        <input
          list={`spots-${r.id}`}
          value={spot}
          onChange={(e) => setSpot(e.target.value)}
          placeholder="např. 7"
          maxLength={64}
          autoFocus
        />
        <datalist id={`spots-${r.id}`}>
          {options
            .filter((o, i, all) => all.findIndex((x) => x.value === o.value) === i)
            .map((o) => (
              <option key={o.value} value={o.value} label={o.label !== o.value ? o.label : undefined} />
            ))}
        </datalist>
      </label>
      {/* The manager, 30 October 2026: "Místo here is the old Zaparkováno; Doporučené místo should be here
          too" — a club booking comes without one, and the check-in sends every such car to the 7th floor.
          The owner, the same day: for customers checking in it is a place at Europort, not Tuchoměřice,
          POP or Kněževes — so the Europort floors only (the value the row stores, '7', 'M', …). */}
      <label>
        Doporučené místo
        <select value={target} onChange={(e) => setTarget(e.target.value)}>
          <option value="">—</option>
          {(spots.data?.europort ?? []).map((e) => (
            <option key={e.value} value={e.value}>
              {e.label}
            </option>
          ))}
          {target && !(spots.data?.europort ?? []).some((e) => e.value === target) && <option value={target}>{target}</option>}
        </select>
      </label>
      {(targetChanged || tm.isSuccess || tm.isError) && (
        <button
          type="button"
          className="btn sm"
          disabled={tm.isPending || !targetChanged}
          title="Uloží jen doporučené místo, vůz zůstane v Příjezdech"
          onClick={() => tm.mutate()}
        >
          {tm.isPending ? 'Ukládám…' : !targetChanged && tm.isSuccess ? '✔ Místo uloženo' : 'Uložit jen místo'}
        </button>
      )}
      {tm.isError && (
        <span className="arrive-error" role="alert">
          {tm.error instanceof ApiError ? tm.error.serverMessage ?? tm.error.message : 'Uložení se nepovedlo.'}
        </span>
      )}
      <label>
        KeyCafe
        <input
          value={keycafe}
          onChange={(e) => setKeycafe(e.target.value)}
          placeholder="kód boxu"
          maxLength={8}
          pattern="[A-Za-z0-9-]{1,8}"
        />
      </label>
      {/* The legacy's second arrival button, "Přijel + klíče" (q_verified_carkey),
          as a tick on the one form: "Klíče od vozidla máme." */}
      <label className="chk">
        <input type="checkbox" checked={carkey} onChange={(e) => setCarkey(e.target.checked)} />
        Klíče od vozidla máme
      </label>
      {owed > 0 && (
        <>
          <span className="depart-owed">
            Zaplatit: <strong>{kc(owed)}</strong>
            {r.servicesPrice > 0 && <span className="was"> (parkování {kc(r.price)} + služby {kc(r.servicesPrice)})</span>}
          </span>
          {/* The old strip's "☐ Platba: [ ] Kč [Hotově]" — a payment is taken
              only when the box is ticked. Two differently named Uložit
              buttons were the alternative, and the manager pressed the one
              that paid (22 October 2026: "it marked it paid by cash by
              itself"). One Uložit; the tick decides. */}
          <label className="chk">
            <input type="checkbox" checked={pay} onChange={(e) => setPay(e.target.checked)} />
            Platba
          </label>
          {pay && (
            <>
              <label>
                Částka
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  step={1}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Kč"
                />
              </label>
              <label>
                Způsob
                <select value={method} onChange={(e) => setMethod(e.target.value as 'cash' | 'card')}>
                  <option value="cash">Hotově</option>
                  <option value="card">Kartou</option>
                </select>
              </label>
            </>
          )}
        </>
      )}
      {/* "Uložit", as the legacy inline form said — the desk never had a
          button called Přijel: they opened the booking, filled the spot and
          saved (the dispatcher, first day of writes, 20 October 2026). The
          save IS the arrival: status verified and the stamp, or at Europort
          the stamp alone (F-06). With Platba ticked it also takes the money. */}
      {/* No Uložit without a spot (the manager's re-test, 25 October 2026):
          saved without one the car fell out of every list. The button
          stays disabled and says why, so the dispatcher sees what is
          missing; the server refuses it too. */}
      <button type="submit" className="btn primary" disabled={m.isPending || !spot.trim() || (pay && !(Number.isInteger(Number(amount)) && Number(amount) > 0))} title={!spot.trim() ? 'Nejdřív vyberte místo stání' : pay ? 'Uložit — vůz přijel, platba přijata' : 'Uložit — vůz přijel'}>
        {m.isPending ? 'Ukládám…' : pay ? 'Uložit (zaplaceno)' : 'Uložit'}
      </button>
      {!spot.trim() && <span className="arrive-error">Vyberte místo stání</span>}
      {/* Opened by a button, the form can be closed again; opened with the
          row (defaultOpen) there is nothing to cancel, and a "Zrušit" beside
          Uložit reads as cancelling the booking (the owner, 20 October). */}
      {!defaultOpen && (
        <button type="button" className="btn" onClick={() => setOpen(false)} disabled={m.isPending}>
          Zrušit
        </button>
      )}
      {message && <span className="arrive-error">{message}</span>}
    </form>
  );
}
